import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { api, formatPrice } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ArtworkManager } from "../../components/ArtworkManager";

const TABS = ["Œuvres", "Commandes", "Messages"];

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Œuvres");
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/contact").then((r) => setMessages(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading || !user || user.role !== "admin") return <div className="pt-40 text-center text-zinc-500">Chargement…</div>;

  return (
    <div className="min-h-screen pt-24 pb-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-display text-4xl text-white">Tableau de bord</h1>
          <p className="text-zinc-500 text-sm mt-1">{user.email}</p>
        </div>
        <button data-testid="logout-button" onClick={() => { logout(); navigate("/admin/login"); }}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-5 py-2.5 text-sm transition-colors">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>

      <div className="flex gap-2 border-b border-white/10 mb-8">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === t ? "border-[#2563EB] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Œuvres" && <ArtworkManager defaultArtistName="" />}

      {tab === "Commandes" && (
        <div data-testid="orders-tab">
          {orders.length === 0 ? (
            <p className="text-zinc-500">Aucune commande pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.session_id} className="border border-white/10 bg-[#0d0d0d] p-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-white">{o.titles?.join(", ") || "—"}</p>
                    <p className="text-zinc-500 text-sm">{o.customer_name} · {o.customer_email}</p>
                    {o.line_items?.some((li) => li.artist_iban) && (
                      <p className="text-zinc-600 text-xs mt-1">
                        Reversement artiste : {o.line_items.filter((li) => li.artist_iban).map((li) => `${li.artist_name} (${li.artist_iban})`).join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-white">{formatPrice(o.amount)}</p>
                    <span className={`text-xs uppercase tracking-wide ${o.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>
                      {o.payment_status === "paid" ? "Payée" : o.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Messages" && (
        <div data-testid="messages-tab">
          {messages.length === 0 ? (
            <p className="text-zinc-500">Aucun message reçu.</p>
          ) : (
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="border border-white/10 bg-[#0d0d0d] p-5">
                  <p className="text-white">{m.name} <span className="text-zinc-500 text-sm">· {m.email}</span></p>
                  {m.subject && <p className="text-[#60a5fa] text-sm mt-1">{m.subject}</p>}
                  <p className="text-zinc-300 text-sm mt-2">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
